import React from 'react';
import { Box, Typography } from '@mui/material';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import VideocamIcon from '@mui/icons-material/Videocam';
import ImageIcon from '@mui/icons-material/Image';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

export type MediaTypeFilterValue = 'all' | 'video' | 'photo';

interface MediaTypeFilterProps {
  value: MediaTypeFilterValue;
  onChange: (value: MediaTypeFilterValue) => void;
  counts?: { all: number; video: number; photo: number };
  disabled?: boolean;
}

const OPTIONS: {
  value: MediaTypeFilterValue;
  label: string;
  aria: string;
  Icon: React.ElementType;
}[] = [
  { value: 'all', label: 'All', aria: 'Show all media', Icon: FilterNoneIcon },
  { value: 'video', label: 'Videos', aria: 'Show videos only', Icon: VideocamIcon },
  { value: 'photo', label: 'Photos', aria: 'Show photos only', Icon: ImageIcon },
];

const countLabel = (
  value: MediaTypeFilterValue,
  counts: { all: number; video: number; photo: number }
): string => {
  if (value === 'all') return `${counts.all} items`;
  if (value === 'video') return `${counts.video} videos`;
  return `${counts.photo} photos`;
};

const MediaTypeFilter: React.FC<MediaTypeFilterProps> = ({
  value,
  onChange,
  counts,
  disabled,
}) => {
  const reduceMotion = useReducedMotion();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.5,
        mb: 2.5,
      }}
    >
      <Box sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}>
        <AnimatePresence mode="wait" initial={false}>
          <Typography
            component={motion.span}
            key={value}
            variant="body2"
            initial={reduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            sx={{
              color: '#71767B',
              fontWeight: 500,
              letterSpacing: '0.01em',
              display: 'inline-block',
            }}
          >
            {counts ? countLabel(value, counts) : 'Filter media'}
          </Typography>
        </AnimatePresence>
      </Box>

      <Box
        role="radiogroup"
        aria-label="Filter media by type"
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          alignSelf: { xs: 'stretch', sm: 'auto' },
          p: 0.5,
          gap: 0.5,
          borderRadius: 9999,
          border: '1px solid #2F3336',
          backgroundColor: '#0F1419',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
          opacity: disabled ? 0.55 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          minWidth: { sm: 288 },
        }}
      >
        {OPTIONS.map(({ value: option, label, aria, Icon }) => {
          const selected = value === option;
          const optionCount = counts?.[option];

          return (
            <Box
              key={option}
              component={motion.button}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={aria}
              disabled={disabled}
              onClick={() => {
                if (!selected) onChange(option);
              }}
              whileTap={reduceMotion || disabled ? undefined : { scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 480, damping: 32 }}
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                minHeight: 36,
                px: { xs: 1, sm: 1.5 },
                border: 0,
                borderRadius: 9999,
                background: 'transparent',
                color: selected ? '#E7E9EA' : '#71767B',
                cursor: disabled ? 'default' : 'pointer',
                fontFamily: 'inherit',
                fontSize: '0.8125rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
                lineHeight: 1,
                WebkitTapHighlightColor: 'transparent',
                transition: 'color 160ms ease',
                '&:hover': {
                  color: selected ? '#E7E9EA' : '#E7E9EA',
                },
                '&:focus-visible': {
                  outline: '2px solid #1D9BF0',
                  outlineOffset: 2,
                },
              }}
            >
              {selected && (
                <Box
                  component={motion.span}
                  layoutId={reduceMotion ? undefined : 'media-type-pill'}
                  transition={
                    reduceMotion
                      ? { duration: 0 }
                      : { type: 'spring', stiffness: 420, damping: 34, mass: 0.7 }
                  }
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 9999,
                    backgroundColor: '#1A2332',
                    border: '1px solid rgba(29, 155, 240, 0.55)',
                    boxShadow:
                      '0 0 0 1px rgba(29, 155, 240, 0.08), 0 4px 12px rgba(0, 0, 0, 0.28)',
                    zIndex: -1,
                  }}
                />
              )}

              <Icon
                sx={{
                  fontSize: 16,
                  color: selected ? '#1D9BF0' : 'inherit',
                  transition: 'color 160ms ease, transform 180ms ease',
                  transform: selected ? 'scale(1.06)' : 'scale(1)',
                }}
              />
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                {label}
              </Box>
              {typeof optionCount === 'number' && (
                <Box
                  component={motion.span}
                  key={`${option}-${optionCount}`}
                  initial={reduceMotion ? false : { opacity: 0.55, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.16, ease: 'easeOut' }}
                  sx={{
                    minWidth: 20,
                    px: 0.6,
                    py: 0.15,
                    borderRadius: 9999,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    lineHeight: 1.35,
                    color: selected ? '#E7E9EA' : '#71767B',
                    backgroundColor: selected
                      ? 'rgba(29, 155, 240, 0.18)'
                      : 'rgba(47, 51, 54, 0.85)',
                    border: selected
                      ? '1px solid rgba(29, 155, 240, 0.35)'
                      : '1px solid transparent',
                    transition: 'color 160ms ease, background-color 160ms ease, border-color 160ms ease',
                  }}
                >
                  {optionCount}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MediaTypeFilter;
